import React from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Chip
} from '@mui/material';
import { DialogState, SimpleDialog } from '../../../../toolympus/components/primitives';
import { Employee } from '../../Structure/types';
import { AbsenceStats, AbsenceEventSchema } from '../data/types';
import { getAbsenceKindTitle } from './EditAbsenceDialog';
import { TimeDisplay } from '../../../../toolympus/components/schemed/TimeDisplay';

interface Props extends Omit<DialogState, "open"> {
  item: { stats?: AbsenceStats, employee: Employee } | undefined | null;
  isLoading?: boolean;
}

export const AbsenceStatsDialog = (props: Props) => {
  const { item, isLoading } = props;
  const { stats, employee } = item || {};

  // 1. Format the title with Employee name and Year
  const fullName = employee ? `${employee.first_name} ${employee.last_name || ''}`.trim() : '';
  const year = stats?.year ? ` ${stats.year}` : '';
  const title = `Статистика ${year}: ${fullName}`;

  // Extract all valid kinds from schema
  const allKinds = AbsenceEventSchema.kind.values?.map(v => v.value) || [];

  return (
    <SimpleDialog
      {...props}
      dialogTitle={title}
      save={() => {}}
      noSave
      cancelLabel="Закрыть"
      maxWidth="xs"
      noFullscreen
    >
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      ) : stats ? (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, bgcolor: 'background.default' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell align="right"><strong>Дни</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allKinds.map((kind) => {
                const kindData = stats.stats[kind] as {
                  taken?: number;
                  limit?: number;
                  extra?: number;
                  remaining?: number;
                  recent_dates?: string[];
                  taken_in_window?: number;
                } | undefined;
                
                const hasDayoffDetails = kindData?.limit !== undefined;
                const recentDates = kindData?.recent_dates;
                const hasRemoteDetails = Boolean(recentDates !== undefined);
                const hasSubrows = hasDayoffDetails || hasRemoteDetails;
                const taken = kindData?.taken || 0;

                return (
                  <React.Fragment key={kind}>
                    <TableRow sx={{ '& > td': { borderBottom: hasSubrows ? 'none' : undefined } }}>
                      <TableCell sx={{ color: taken > 0 ? 'text.primary' : 'text.secondary' }}>
                        {getAbsenceKindTitle(kind)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: taken > 0 ? 500 : 400, color: taken > 0 ? 'text.primary' : 'text.secondary' }}>
                        {taken}
                      </TableCell>
                    </TableRow>

                    {/* Sub-rows for Dayoffs */}
                    {hasDayoffDetails && (
                      <>
                        <TableRow sx={{ '& > td': { borderBottom: 'none', py: 0.5 } }}>
                          <TableCell sx={{ pl: 4, color: 'text.secondary', border: 'none' }}>
                            <Typography variant="body2">Лимит в год</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary', border: 'none' }}>
                            {kindData?.limit}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '& > td': { borderBottom: 'none', py: 0.5 } }}>
                          <TableCell sx={{ pl: 4, color: 'text.secondary', border: 'none' }}>
                            <Typography variant="body2">Из них дополнительно</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary', border: 'none' }}>
                            {kindData?.extra || 0}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ pl: 4 }}>
                            <Typography variant="body2">Доступный остаток</Typography>
                          </TableCell>
                          <TableCell 
                            align="right" 
                            sx={{ 
                              fontWeight: 600, 
                              color: kindData?.remaining === 0 ? 'error.main' : 'success.main' 
                            }}
                          >
                            {kindData?.remaining}
                          </TableCell>
                        </TableRow>
                      </>
                    )}

                    {/* Sub-rows for Remotes (28-day rolling window) */}
                    {hasRemoteDetails && (
                      <>
                        <TableRow sx={{ '& > td': { borderBottom: 'none', py: 0.5 } }}>
                          <TableCell sx={{ pl: 4, color: 'text.secondary', border: 'none' }}>
                            <Typography variant="body2">За последние 28 дней</Typography>
                          </TableCell>
                          <TableCell 
                            align="right" 
                            sx={{ 
                              border: 'none',
                              color: (recentDates?.length || 0) >= 8 ? 'error.main' : 'text.secondary'
                            }}
                          >
                            {recentDates?.length ?? 0} / 8
                          </TableCell>
                        </TableRow>
                        {!!recentDates?.length && <TableRow>
                          <TableCell colSpan={2} sx={{ pl: 4, pt: 0.5, pb: 1.5 }}>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {recentDates.map((dateStr) => (
                                <Chip 
                                  key={dateStr} 
                                  label={<TimeDisplay field="date" row={{ date: dateStr }} schema={{}} config={{ formatAsDate: true }} />} 
                                  size="small" 
                                  variant="outlined" 
                                  sx={{ fontSize: '0.75rem', height: 22 }}
                                />
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
          Нет данных для отображения
        </Typography>
      )}
    </SimpleDialog>
  );
};